import React from 'react';
import { Button, Spinner, Text } from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { Box, useFluentTokens } from '../../../../../../theme/fluent';
import { ScrollableDialog } from '../../../ScrollableDialog';

const RichTextEditor = React.lazy(() =>
    import('../../../../display/RichTextEditor').then((m) => ({ default: m.RichTextEditor })),
);

type StateComposeProps = {
    open: boolean;
    meetingTitle: string;
    selectedCount: number;
    isEmpty: boolean;
    message: string;
    onOpenChange: (open: boolean) => void;
    onClose: () => void;
    onBack: () => void;
    onSend: () => void;
    onMessageChange: (message: string) => void;
    onIsEmptyChange: (isEmpty: boolean) => void;
}

export const StateCompose: React.FC<StateComposeProps> = ({
    open,
    meetingTitle,
    selectedCount,
    isEmpty,
    message,
    onOpenChange,
    onClose,
    onBack,
    onSend,
    onMessageChange,
    onIsEmptyChange,
}) => {
    const { tokens, modeTokens } = useFluentTokens();

    return (
        <ScrollableDialog
            open={open}
            onOpenChange={(_, data) => onOpenChange(data.open)}
            title={meetingTitle}
            titleAction={
                <Button appearance="subtle" icon={<Dismiss24Regular />} onClick={onClose} aria-label="Close" />
            }
            actionsLeft={
                <Button
                    appearance="secondary"
                    onClick={onBack}
                    style={{
                        borderRadius: tokens.borderRadiusXXXLarge,
                        fontWeight: tokens.fontWeightSemibold,
                    }}
                >
                    Back
                </Button>
            }
            actionsRight={
                <Button
                    appearance="primary"
                    onClick={onSend}
                    disabled={isEmpty}
                    style={{
                        borderRadius: tokens.borderRadiusXXXLarge,
                        fontWeight: tokens.fontWeightSemibold,
                    }}
                >
                    Send reminder
                </Button>
            }
            size="medium"
        >
            <Box style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL }}>
                <Text size={400} weight="semibold">
                    This reminder will be sent to {selectedCount} selected recipient{selectedCount === 1 ? '' : 's'}
                </Text>

                <Box style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS }}>
                    <Text size={300} style={{ color: modeTokens.colorNeutralForeground1 }}>
                        Customise the message that will be sent via Outlook:
                    </Text>
                    <React.Suspense fallback={<Spinner role="progressbar" size="tiny" aria-label="Loading" />}>
                        <RichTextEditor
                            value={message}
                            onChange={onMessageChange}
                            onIsEmptyChange={onIsEmptyChange}
                            placeholder="Enter your reminder message..."
                            minHeight="170px"
                            showToolbar={true}
                            enableLists={true}
                            enableLinkInsertion={true}
                            ariaLabel="Reminder message content"
                        />
                    </React.Suspense>
                </Box>
            </Box>
        </ScrollableDialog>
    );
};
